/**
 * @fileoverview StatusDistributionChart - Pie/Donut chart showing run status breakdown
 * Displays the distribution of runs across different statuses
 */
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from 'recharts'
import { chartDefaults, tooltipStyles } from './chartConfig'

export interface StatusDataPoint {
  status: string
  count: number
  color: string
  [key: string]: string | number
}

export interface StatusDistributionChartProps {
  /** Array of status data points */
  data: StatusDataPoint[]
  /** Chart height in pixels */
  height?: number
  /** Show legend */
  showLegend?: boolean
  /** Variant: pie (solid) or donut (with hole) */
  variant?: 'pie' | 'donut'
  /** Show total in center (only for donut variant) */
  showCenterLabel?: boolean
  /** Custom center label text */
  centerLabelText?: string
}

export function StatusDistributionChart({
  data,
  height = chartDefaults.height,
  showLegend = true,
  variant = 'donut',
  showCenterLabel = false,
  centerLabelText,
}: StatusDistributionChartProps) {
  // Check for empty or zero data
  const totalCount = data.reduce((sum, item) => sum + item.count, 0)
  const hasData = data.length > 0 && totalCount > 0

  if (!hasData) {
    return (
      <div
        style={{ height }}
        className="flex items-center justify-center text-muted-foreground"
        role="img"
        aria-label="Status distribution chart - no data available"
      >
        <p>No data available</p>
      </div>
    )
  }

  // Inner/outer radius for pie vs donut
  const innerRadius = variant === 'donut' ? 60 : 0
  const outerRadius = 80

  return (
    <div style={{ height }} role="img" aria-label="Status distribution chart">
      <ResponsiveContainer width="100%" height="100%" minHeight={1}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            paddingAngle={2}
            dataKey="count"
            nameKey="status"
            label={false}
            labelLine={false}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={tooltipStyles.contentStyle}
            formatter={(value, name, props) => {
              const percent = props?.payload?.percent
              const percentStr = percent ? ` (${(percent * 100).toFixed(0)}%)` : ''
              return [`${value ?? 0}${percentStr}`, name ?? '']
            }}
          />
          {showLegend && (
            <Legend
              verticalAlign="bottom"
              height={36}
              formatter={(value: string) => (
                <span className="text-foreground text-sm">{value}</span>
              )}
            />
          )}
          {/* Center label for donut chart */}
          {showCenterLabel && variant === 'donut' && (
            <text
              x="50%"
              y="50%"
              textAnchor="middle"
              dominantBaseline="middle"
            >
              <tspan
                x="50%"
                dy="-0.5em"
                className="fill-foreground text-2xl font-bold"
              >
                {totalCount}
              </tspan>
              {centerLabelText && (
                <tspan
                  x="50%"
                  dy="1.5em"
                  className="fill-muted-foreground text-xs"
                >
                  {centerLabelText}
                </tspan>
              )}
            </text>
          )}
        </PieChart>
      </ResponsiveContainer>
    </div>
  )
}
